import { useState } from 'react';
import { Button, Card, Field, Page } from '@iqz-ddds/components';
import { FailureNotice } from '../components/FailureNotice';

export interface IdentifyScreenProps {
  readonly repName?: string | undefined;
  /** Sends the OTP code to `email` and advances to the code screen. Does not upload anything. */
  readonly onSubmit: (input: { readonly displayName: string; readonly email: string }) => Promise<void>;
}

function isValidEmail(email: string): boolean {
  const atIndex = email.indexOf('@');
  const domain = email.slice(atIndex + 1);
  return atIndex > 0
    && atIndex === email.lastIndexOf('@')
    && !/\s/.test(email)
    && domain.includes('.')
    && !domain.startsWith('.')
    && !domain.endsWith('.');
}

export function IdentifyScreen({ repName, onSubmit }: IdentifyScreenProps): React.JSX.Element {
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async (): Promise<void> => {
    if (displayName.trim() === '' || !isValidEmail(email.trim())) {
      setError(new Error('Enter your name and a valid email address.'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ displayName: displayName.trim(), email: email.trim() });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Something went wrong.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page eyebrow="Secure upload" title={repName ? `Send documents to ${repName}` : 'Send documents securely'}>
      <FailureNotice error={error} />
      <Card>
        <form onSubmit={(event) => { event.preventDefault(); void submit(); }}>
          <Field id="display-name" label="Your name" value={displayName} maxLength={200} autoComplete="name" disabled={busy} onChange={setDisplayName} />
          <Field id="email" label="Your email address" value={email} maxLength={254} inputMode="email" autoComplete="email" disabled={busy} onChange={setEmail} />
          <Button type="submit" disabled={busy}>{busy ? 'Sending code…' : 'Send verification code'}</Button>
        </form>
      </Card>
      <p className="ddds-footer">We will send a six-digit code to this address before you can upload anything.</p>
    </Page>
  );
}